import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageWrapper from '../PageWrapper/PageWrapper';
import { AdminRestricted } from '../../components/AdminRestricted/AdminRestricted';
import { CardV2, CardHeader, CardTitle, CardContent } from '../../components/CardV2/CardV2';
import { TabV2, TabsContent, TabsList, TabsTrigger } from '../../components/TabV2/TabV2';
import { Calendar, Users, AlertTriangle, ClipboardList } from 'lucide-react';
import MeetingsTotalOverTime from './graphs/MeetingsTotalOverTime';
import MeetingsTypeDistribution from './graphs/MeetingsTypeDistribution';
import NoShowsRatioCard from './graphs/NoShowsRatioCard';
import NoShowsOverTime from './graphs/NoShowsOverTime';
import BookingsTotalPendingCard from './graphs/BookingsTotalPendingCard';
import BookingsTypeDistribution from './graphs/BookingsTypeDistribution';
import BookingsCancellationRatioCard from './graphs/BookingsCancellationRatioCard';
import SectionTitle from '../../components/SectionTitle/SectionTitle';
import BookingsInboundChannels from './graphs/BookingsInboundChannels';
import BookingsPendingPriceRanges from './graphs/BookingsPendingPriceRanges';

type TimeRange = {
  start: string; // ISO date string
  end: string;   // ISO date string
};

const toISODate = (d: Date) => {
  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);
  return dd.toISOString();
};

function MeetingsStatisticsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'meetings' | 'bookings'>('meetings');

  const monthStart = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const monthEnd = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1, 0);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [timeRange, setTimeRange] = useState<TimeRange>({ start: toISODate(monthStart), end: toISODate(monthEnd) });

  const onDateChange = (key: 'start' | 'end', value: string) => {
    // value comes as yyyy-mm-dd; convert to ISO
    const date = new Date(value);
    setTimeRange((prev) => ({ ...prev, [key]: toISODate(date) }));
  };

  const startInputValue = useMemo(() => new Date(timeRange.start).toISOString().slice(0, 10), [timeRange.start]);
  const endInputValue = useMemo(() => new Date(timeRange.end).toISOString().slice(0, 10), [timeRange.end]);

  return (
    <PageWrapper>
      <AdminRestricted>
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              <h1 className="text-2xl font-bold text-foreground">{t('stats.meetings_bookings.title') || 'Meetings & Bookings Insights'}</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t('stats.filters.from') || 'From'}</span>
                <input type="date" value={startInputValue} onChange={(e) => onDateChange('start', e.target.value)} className="px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t('stats.filters.to') || 'To'}</span>
                <input type="date" value={endInputValue} onChange={(e) => onDateChange('end', e.target.value)} className="px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground" />
              </div>
            </div>
          </div>

          <TabV2 value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="meetings" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                {t('stats.meetings') || 'Meetings'}
              </TabsTrigger>
              <TabsTrigger value="bookings" className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4" />
                {t('stats.bookings') || 'Bookings'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="meetings" className="space-y-6">
              <SectionTitle text={t('stats.meetings') || 'Meetings'} />
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-6">
                  <CardV2>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        {t('stats.meetings_total_over_time') || 'Total Meetings Over Time'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <MeetingsTotalOverTime timeRange={timeRange} />
                    </CardContent>
                  </CardV2>
                </div>
                <div className="lg:col-span-2 space-y-6">
                  <CardV2>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        {t('stats.meetings_type_distribution') || 'Meetings Type Distribution'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <MeetingsTypeDistribution timeRange={timeRange} />
                    </CardContent>
                  </CardV2>
                </div>
              </div>

              <SectionTitle text={t('stats.meetings_no_shows') || 'No shows'} cssStyles="mt-6" />
              <div className="mb-4 p-3 bg-muted/50 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">
                  {t('stats.meetings_no_shows_note') || 'To start tracking no-shows, mark meetings as no-shows in the calendar using the icon in the top right corner of each meeting.'}
                </p>
              </div>
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-6">
                  <CardV2>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" />
                        {t('stats.meetings_noshow_ratio') || 'No-Shows vs Total'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <NoShowsRatioCard timeRange={timeRange} />
                    </CardContent>
                  </CardV2>
                </div>
                <div className="lg:col-span-2">
                  <CardV2>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" />
                        {t('stats.meetings_noshow_over_time') || 'No-Shows Over Time'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <NoShowsOverTime timeRange={timeRange} />
                    </CardContent>
                  </CardV2>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="bookings" className="space-y-6">
              <SectionTitle text={t('stats.bookings') || 'Bookings'} />
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-6">
                  <CardV2>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <ClipboardList className="h-5 w-5" />
                        {t('stats.bookings_total_pending') || 'Total Pending Bookings'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <BookingsTotalPendingCard timeRange={timeRange} />
                    </CardContent>
                  </CardV2>
                  <CardV2>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" />
                        {t('stats.bookings_cancellation_ratio') || 'Cancelled vs Total'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <BookingsCancellationRatioCard timeRange={timeRange} />
                    </CardContent>
                  </CardV2>
                </div>
                <div className="lg:col-span-2">
                  <CardV2>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <ClipboardList className="h-5 w-5" />
                        {t('stats.bookings_type_distribution') || 'Bookings Type Distribution'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <BookingsTypeDistribution timeRange={timeRange} />
                    </CardContent>
                  </CardV2>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <CardV2>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" />
                      {t('stats.bookings_inbound_channels') || 'Bookings inbound channels'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BookingsInboundChannels timeRange={timeRange} />
                  </CardContent>
                </CardV2>
                <CardV2>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" />
                      {t('stats.bookings_price_ranges') || 'Bookings price ranges'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BookingsPendingPriceRanges timeRange={timeRange} />
                  </CardContent>
                </CardV2>
              </div>
            </TabsContent>
          </TabV2>
        </div>
      </AdminRestricted>
    </PageWrapper>
  );
}

export default MeetingsStatisticsPage;


